import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { applicationBuilderAPI } from '../../api/applicationBuilderAPI';
import './FormStyles.css';
import './PersonalStatementFormStyles.css';

const PersonalStatementForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    subject: '',
    question_1: '',
    question_2: '',
    question_3: ''
  });
  const [feedback, setFeedback] = useState({});
  const [loadingFeedback, setLoadingFeedback] = useState({});
  const [oxbridgeDepth, setOxbridgeDepth] = useState(false);
  const [graphMeta, setGraphMeta] = useState({});
  const [versions, setVersions] = useState([]);
  const [downloadingVersion, setDownloadingVersion] = useState(null);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const response = await applicationBuilderAPI.getProfile();
        if (response.profile_data) {
          setProfileData(response.profile_data);
          setFormData(prev => ({
            ...prev,
            first_name: response.profile_data.first_name || '',
            last_name: response.profile_data.last_name || '',
            email: response.profile_data.email || '',
            phone: response.profile_data.phone || '',
            subject: response.profile_data.primary_subject || ''
          }));
        }
      } catch (error) {
        console.error('Error loading profile:', error);
      }
    };
    loadProfile();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });

    if (field.startsWith('question_')) {
      // Clear feedback for this question when the content changes
      setFeedback((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
      setGraphMeta((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const getCounts = (text) => {
    const value = text || '';
    const chars = value.length;
    const words = value.trim() ? value.trim().split(/\s+/).length : 0;
    return { chars, words };
  };

  const getCountStatus = (chars) => {
    if (!chars) return 'neutral';
    if (chars < 50) return 'error';
    return 'ok';
  };

  const [loadingAllFeedback, setLoadingAllFeedback] = useState(false);

  const alertSaveFailed = (error, fallback) => {
    if (error?.message === 'popup') {
      alert('Allow pop-ups for this site to open documents.');
      return;
    }
    if (error?.data?.error === 'save_failed') {
      alert(error.data.message);
      return;
    }
    alert(fallback);
  };

  const refreshVersions = useCallback(async () => {
    try {
      const response = await applicationBuilderAPI.listPersonalStatementVersions(
        formData.first_name,
        formData.last_name
      );
      setVersions(response.versions || []);
    } catch (error) {
      console.error('Error loading personal statement versions:', error);
    }
  }, [formData.first_name, formData.last_name]);

  useEffect(() => {
    // Names are typed, so wait for a pause before asking the server again.
    const timer = setTimeout(refreshVersions, 400);
    return () => clearTimeout(timer);
  }, [refreshVersions]);

  const downloadVersion = (id) => {
    setDownloadingVersion(id);
    applicationBuilderAPI.openBuilderDocument(id)
      .catch((error) => {
        console.error('Error downloading personal statement version:', error);
        alertSaveFailed(error, 'Failed to download that version. Please try again.');
      })
      .finally(() => setDownloadingVersion(null));
  };

  const formatVersionDate = (savedAt) => {
    const date = new Date(savedAt);
    if (Number.isNaN(date.getTime())) return savedAt;
    return date.toLocaleString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const buildPsSnapshot = (feedbackOverride = feedback) => ({
    first_name: formData.first_name || '',
    last_name: formData.last_name || '',
    subject: formData.subject || '',
    question_1: formData.question_1 || '',
    question_2: formData.question_2 || '',
    question_3: formData.question_3 || '',
    feedback: feedbackOverride || {},
  });

  const requestFeedback = async (questionKey, questionNum) => {
    const content = formData[questionKey] || '';
    if (!content.trim() || content.length < 50) {
      alert('Please write at least 50 characters before requesting feedback.');
      return;
    }

    setLoadingFeedback((prev) => ({ ...prev, [questionKey]: true }));
    try {
      const response = await applicationBuilderAPI.getFeedback(
        questionNum,
        content,
        formData.subject || '',
        true,
        { oxbridge: oxbridgeDepth, timeoutMs: 55000, snapshot: buildPsSnapshot() }
      );

      if (response.feedback) {
        setFeedback((prev) => ({ ...prev, [questionKey]: response.feedback }));
      }
      setGraphMeta((prev) => ({
        ...prev,
        [questionKey]: {
          band: response.isomorphism?.band,
          delta: response.isomorphism?.delta,
          dbWrite: response.db_write?.status,
          oxbridge: response.oxbridge,
          contentMix: response.content_mix,
          llmPolish: response.llm_polish,
        },
      }));
      if (response.ps_docx) {
        refreshVersions();
      }
    } catch (error) {
      console.error('Error getting feedback:', error);
      alertSaveFailed(error, 'Failed to get feedback. Please try again.');
    } finally {
      setLoadingFeedback((prev) => ({ ...prev, [questionKey]: false }));
    }
  };

  const requestAllFeedback = async () => {
    const ready = [1, 2, 3].filter((n) => {
      const t = formData[`question_${n}`] || '';
      return t.trim().length >= 50;
    });
    if (!ready.length) {
      alert('Write at least 50 characters in one or more questions first.');
      return;
    }

    setLoadingAllFeedback(true);
    setLoadingFeedback({
      question_1: ready.includes(1),
      question_2: ready.includes(2),
      question_3: ready.includes(3),
    });
    try {
      const response = await applicationBuilderAPI.getAllPsFeedback({
        question1: formData.question_1,
        question2: formData.question_2,
        question3: formData.question_3,
        subject: formData.subject || '',
        oxbridge: oxbridgeDepth,
        snapshot: buildPsSnapshot(),
      });
      const byQ = response.feedback_by_question || {};
      const meta = response.meta_by_question || {};
      const scored = response.questions_scored || Object.keys(byQ);
      const errors = response.errors_by_question || {};
      if (!scored.length) {
        throw new Error('No questions were scored');
      }
      setFeedback((prev) => ({ ...prev, ...byQ }));
      setGraphMeta((prev) => {
        const next = { ...prev };
        Object.keys(meta).forEach((key) => {
          const m = meta[key] || {};
          next[key] = {
            band: m.isomorphism?.band,
            delta: m.isomorphism?.delta,
            dbWrite: m.db_write?.status,
            oxbridge: response.oxbridge,
            llmPolish: m.llm_polish,
            qualityScore: m.quality_score,
          };
        });
        return next;
      });
      const errKeys = Object.keys(errors);
      if (errKeys.length) {
        console.warn('Partial PS feedback errors:', errors);
      }
      if (response.ps_docx) {
        refreshVersions();
      }
    } catch (error) {
      console.error('Error getting all-question feedback:', error);
      if (error?.data?.error === 'save_failed') {
        alert(error.data.message);
      } else {
        alert(
          error?.data?.feedback
            || error?.message
            || 'Failed to get full personal statement feedback. Please try again.'
        );
      }
    } finally {
      setLoadingAllFeedback(false);
      setLoadingFeedback({});
    }
  };

  const counts = {
    question_1: getCounts(formData.question_1),
    question_2: getCounts(formData.question_2),
    question_3: getCounts(formData.question_3),
  };

  const statuses = {
    question_1: getCountStatus(counts.question_1.chars),
    question_2: getCountStatus(counts.question_2.chars),
    question_3: getCountStatus(counts.question_3.chars),
  };

  const renderFeedbackList = (text) => {
    if (!text) return null;
    const rawLines = text.split(/\r?\n/);
    const blocks = [];
    let listBuffer = [];

    const flushList = () => {
      if (!listBuffer.length) return;
      blocks.push({ type: 'ul', items: listBuffer });
      listBuffer = [];
    };

    rawLines.forEach((raw) => {
      const line = raw.trimEnd();
      const trimmed = line.trim();
      if (!trimmed) {
        flushList();
        return;
      }
      if (/^#{1,3}\s+/.test(trimmed)) {
        flushList();
        const level = (trimmed.match(/^#+/) || ['#'])[0].length;
        blocks.push({
          type: 'h',
          level: Math.min(level, 3),
          text: trimmed.replace(/^#{1,3}\s+/, '').replace(/\*\*/g, ''),
        });
        return;
      }
      if (/^[-•*]\s+/.test(trimmed)) {
        listBuffer.push(trimmed.replace(/^[-•*]\s+/, '').replace(/\*\*/g, ''));
        return;
      }
      flushList();
      blocks.push({ type: 'p', text: trimmed.replace(/\*\*/g, '') });
    });
    flushList();

    if (!blocks.length) return null;
    return (
      <div className="ps-feedback-body">
        {blocks.map((block, idx) => {
          if (block.type === 'h') {
            const Tag = block.level === 1 ? 'h4' : block.level === 2 ? 'h5' : 'h6';
            return (
              <Tag key={idx} className={`ps-feedback-h ps-feedback-h${block.level}`}>
                {block.text}
              </Tag>
            );
          }
          if (block.type === 'ul') {
            return (
              <ul key={idx} className="ps-feedback-list">
                {block.items.map((item, j) => (
                  <li key={j}>{item}</li>
                ))}
              </ul>
            );
          }
          return (
            <p key={idx} className="ps-feedback-p">
              {block.text}
            </p>
          );
        })}
      </div>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const win = window.open('about:blank', '_blank');
    if (!win) {
      alert('Allow pop-ups for this site to open documents.');
      return;
    }
    win.opener = null;
    setLoading(true);

    try {
      const response = await applicationBuilderAPI.exportPersonalStatement(buildPsSnapshot());
      if (!response.ps_docx) {
        throw new Error('No document id returned for export');
      }
      await applicationBuilderAPI.openBuilderDocument(response.ps_docx, win);
      refreshVersions();
    } catch (error) {
      win.close();
      console.error('Error exporting personal statement:', error);
      alertSaveFailed(error, 'Failed to export statement. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-container personal-statement-form">
      <div className="form-wrapper">
        <div className="form-header">
          <h1>Personal Statement Builder</h1>
          <p>Answer these questions to build your personal statement</p>
        </div>

        <form onSubmit={handleSubmit} className="application-form">
          {/* Personal Information */}
          <section className="form-section">
            <h2 className="section-title">Your Information</h2>
            <div className="ps-user-info-row">
              <div className="input-field">
                <label>First Name</label>
                <input
                  type="text"
                  placeholder="Your first name"
                  value={formData.first_name}
                  onChange={(e) => handleInputChange('first_name', e.target.value)}
                  required
                />
              </div>
              <div className="input-field">
                <label>Last Name</label>
                <input
                  type="text"
                  placeholder="Your last name"
                  value={formData.last_name}
                  onChange={(e) => handleInputChange('last_name', e.target.value)}
                  required
                />
              </div>
              <div className="input-field">
                <label>Email</label>
                <input
                  type="email"
                  placeholder="Your email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  required
                />
              </div>
              <div className="input-field">
                <label>Subject/Course</label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => handleInputChange('subject', e.target.value)}
                  placeholder="e.g., Computer Science, Medicine"
                />
              </div>
            </div>
          </section>

          <section className="form-section">
            <label className="ps-oxbridge-toggle" style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
              <input
                type="checkbox"
                checked={oxbridgeDepth}
                onChange={(e) => setOxbridgeDepth(e.target.checked)}
              />
              <span>
                <strong>Oxbridge / tutorial-style depth</strong>
                <br />
                <span style={{ fontSize: '0.9em', opacity: 0.85 }}>
                  Uses profile-safe depth rules (≈80% academic + super-curricular on Q1/Q2).
                  Does not name a university — keep your statement UCAS-safe.
                </span>
              </span>
            </label>
            <div className="ps-all-feedback-row">
              <button
                type="button"
                className="ps-feedback-button"
                onClick={requestAllFeedback}
                disabled={loadingAllFeedback}
              >
                {loadingAllFeedback
                  ? 'Scoring all questions…'
                  : 'Get feedback for all 3 questions'}
              </button>
              <p className="ps-all-feedback-hint">
                Graph-scores each answer, then one language polish (usually under a minute).
              </p>
            </div>
          </section>

          {/* Questions */}
          <section className="form-section question-section">
            <div className="ps-question-header">
              <h2 className="section-title">Question 1: Motivation and Subject Interest</h2>
              <button
                type="button"
                className="ps-feedback-button"
                onClick={() => requestFeedback('question_1', 1)}
                disabled={loadingFeedback.question_1 || loadingAllFeedback}
              >
                {loadingFeedback.question_1 ? 'Getting feedback…' : 'Get Feedback'}
              </button>
            </div>
            <div className="ps-guidance">
              <div className="ps-guidance-title">What to include:</div>
              <ul className="ps-guidance-list">
                <li><strong>Your motivations</strong>: What inspired your choice? Key moments or role models?</li>
                <li><strong>Subject knowledge</strong>: Books, research, or topics that fascinate you.</li>
                <li><strong>Future plans</strong>: How this course connects to your goals.</li>
              </ul>
            </div>
            <div className="input-field">
              <label>Why do you want to study this course? What motivates you?</label>
              <textarea
                rows="8"
                value={formData.question_1}
                onChange={(e) => handleInputChange('question_1', e.target.value)}
                placeholder="Explain your passion for this subject, what inspired you, and how it connects to your future goals..."
                required
              />
              <div className={`ps-count-row ps-count-${statuses.question_1}`}>
                <span className="ps-count-main">
                  {counts.question_1.chars} characters · {counts.question_1.words} words
                </span>
                <span className="ps-count-min">Min 50 characters</span>
              </div>
              {feedback.question_1 && (
                <div className="ps-feedback-box">
                  <div className="ps-feedback-label">
                    Feedback
                    {graphMeta.question_1?.band ? ` · ${graphMeta.question_1.band}` : ''}
                    {graphMeta.question_1?.dbWrite ? ` · saved: ${graphMeta.question_1.dbWrite}` : ''}
                    {graphMeta.question_1?.llmPolish ? ' · language polish' : ''}
                  </div>
                  {renderFeedbackList(feedback.question_1)}
                </div>
              )}
            </div>
          </section>

          <section className="form-section question-section">
            <div className="ps-question-header">
              <h2 className="section-title">Question 2: Academic Preparation and Skills</h2>
              <button
                type="button"
                className="ps-feedback-button"
                onClick={() => requestFeedback('question_2', 2)}
                disabled={loadingFeedback.question_2 || loadingAllFeedback}
              >
                {loadingFeedback.question_2 ? 'Getting feedback…' : 'Get Feedback'}
              </button>
            </div>
            <div className="ps-guidance">
              <div className="ps-guidance-title">What to include:</div>
              <ul className="ps-guidance-list">
                <li><strong>Academic strengths</strong>: Key subjects, grades, or achievements.</li>
                <li><strong>Skills developed</strong>: Analysis, communication, problem-solving.</li>
                <li><strong>Relevant projects</strong>: Coursework, research, or independent study.</li>
              </ul>
            </div>
            <div className="input-field">
              <label>How have your current studies and experiences prepared you for this course?</label>
              <textarea
                rows="8"
                value={formData.question_2}
                onChange={(e) => handleInputChange('question_2', e.target.value)}
                placeholder="Describe your academic background, relevant qualifications, skills developed, and achievements..."
                required
              />
              <div className={`ps-count-row ps-count-${statuses.question_2}`}>
                <span className="ps-count-main">
                  {counts.question_2.chars} characters · {counts.question_2.words} words
                </span>
                <span className="ps-count-min">Min 50 characters</span>
              </div>
              {feedback.question_2 && (
                <div className="ps-feedback-box">
                  <div className="ps-feedback-label">
                    Feedback
                    {graphMeta.question_2?.band ? ` · ${graphMeta.question_2.band}` : ''}
                    {graphMeta.question_2?.dbWrite ? ` · saved: ${graphMeta.question_2.dbWrite}` : ''}
                    {graphMeta.question_2?.llmPolish ? ' · language polish' : ''}
                  </div>
                  {renderFeedbackList(feedback.question_2)}
                </div>
              )}
            </div>
          </section>

          <section className="form-section question-section">
            <div className="ps-question-header">
              <h2 className="section-title">Question 3: Extracurricular Experiences and Personal Development</h2>
              <button
                type="button"
                className="ps-feedback-button"
                onClick={() => requestFeedback('question_3', 3)}
                disabled={loadingFeedback.question_3 || loadingAllFeedback}
              >
                {loadingFeedback.question_3 ? 'Getting feedback…' : 'Get Feedback'}
              </button>
            </div>
            <div className="ps-guidance">
              <div className="ps-guidance-title">What to include:</div>
              <ul className="ps-guidance-list">
                <li><strong>Work & volunteering</strong>: Internships, jobs, placements, and skills gained.</li>
                <li><strong>Personal experiences</strong>: Challenges, responsibilities, or turning points.</li>
                <li><strong>Activities & achievements</strong>: Sports, societies, leadership roles, competitions.</li>
              </ul>
            </div>
            <div className="input-field">
              <label>What extracurricular activities, work experience, or personal experiences have shaped you?</label>
              <textarea
                rows="8"
                value={formData.question_3}
                onChange={(e) => handleInputChange('question_3', e.target.value)}
                placeholder="Describe relevant experiences, what you learned, skills gained, and how they demonstrate your suitability..."
                required
              />
              <div className={`ps-count-row ps-count-${statuses.question_3}`}>
                <span className="ps-count-main">
                  {counts.question_3.chars} characters · {counts.question_3.words} words
                </span>
                <span className="ps-count-min">Min 50 characters</span>
              </div>
              {feedback.question_3 && (
                <div className="ps-feedback-box">
                  <div className="ps-feedback-label">
                    Feedback
                    {graphMeta.question_3?.band ? ` · ${graphMeta.question_3.band}` : ''}
                    {graphMeta.question_3?.dbWrite ? ` · saved: ${graphMeta.question_3.dbWrite}` : ''}
                    {graphMeta.question_3?.llmPolish ? ' · language polish' : ''}
                  </div>
                  {renderFeedbackList(feedback.question_3)}
                </div>
              )}
            </div>
          </section>

          <button type="submit" className="submit-button" disabled={loading}>
            {loading ? 'Exporting…' : 'Export Statement'}
          </button>
        </form>

        <section className="ps-versions">
          <h2 className="section-title">Previous versions</h2>
          {versions.length === 0 ? (
            <p className="ps-versions-empty">
              Saved versions appear here after you request feedback or export.
            </p>
          ) : (
            <ul className="ps-versions-list">
              {versions.map((version) => (
                <li key={version.id}>
                  <button
                    type="button"
                    className="ps-version-item"
                    onClick={() => downloadVersion(version.id)}
                    disabled={downloadingVersion === version.id}
                  >
                    <span className="ps-version-date">{formatVersionDate(version.saved_at)}</span>
                    <span className="ps-version-action">
                      {downloadingVersion === version.id ? 'Downloading…' : 'Download'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
};

export default PersonalStatementForm;



